/**
 * useChat.js
 *
 * React hook for sending chat messages to any AI provider via the
 * Lambda chat-completion proxy.
 *
 * Supports both streaming (SSE) and non-streaming modes.
 * The hook exposes two response values:
 *   - `response`     – extracted text content string (simple use cases)
 *   - `fullResponse` – raw SDK response object or array of chunks (advanced use)
 *
 * Usage:
 *   const { response, isLoading, error, sendMessage } = useChat('GEMINI', 'gemini/gemini-2.5-flash');
 *   await sendMessage([{ role: 'user', content: 'Hello!' }]);
 */

import { useState, useCallback } from 'react';
import { getChatCompletion, getStreamingChatCompletion } from '../services/aiIntegrations/chatCompletion';

/**
 * Unified chat hook — works with any provider supported by the Lambda proxy.
 *
 * @param {string}  provider          - Provider identifier (e.g. 'GEMINI', 'OPENAI')
 * @param {string}  model             - Model name
 * @param {boolean} [streaming=true]  - Enable SSE streaming mode
 * @returns {{
 *   response: string,
 *   fullResponse: object|Array|null,
 *   isLoading: boolean,
 *   error: Error|null,
 *   sendMessage: (messages: Array, options?: object) => Promise<void>
 * }}
 */
export function useChat(provider, model, streaming = true) {
  const [response, setResponse] = useState('');
  const [fullResponse, setFullResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Sends a message array to the AI provider.
   * Resets state before each call so stale data is never shown.
   *
   * @param {Array}  messages       - OpenAI-style messages array
   * @param {object} [options={}]   - Extra parameters forwarded to the Lambda
   */
  const sendMessage = useCallback(
    async (messages, options = {}) => {
      // Reset state for the new request
      setResponse('');
      setFullResponse(streaming ? [] : null);
      setIsLoading(true);
      setError(null);

      try {
        if (streaming) {
          // Streaming mode: accumulate chunks as they arrive
          await getStreamingChatCompletion(
            provider,
            model,
            messages,
            (chunk) => {
              // Store the full chunk for advanced consumers
              setFullResponse((prev) => [...prev, chunk]);

              // Extract and append the text delta for simple consumers
              const content = chunk?.choices?.[0]?.delta?.content;
              if (content) {
                setResponse((prev) => prev + content);
              }
            },
            () => setIsLoading(false),   // onComplete
            (err) => {
              setError(err);
              setIsLoading(false);
            },
            options
          );
        } else {
          // Non-streaming mode: single request/response
          const result = await getChatCompletion(provider, model, messages, options);

          setFullResponse(result);
          setResponse(result?.choices?.[0]?.message?.content || '');
          setIsLoading(false);
        }
      } catch (err) {
        setError(err);
        setIsLoading(false);
      }
    },
    [provider, model, streaming]
  );

  return { response, fullResponse, isLoading, error, sendMessage };
}